import React, { useState, lazy, Suspense } from 'react';
import { AuthUser } from '@aws-amplify/auth';
import GenericCoralModal from '../../GenericCoralModal/GenericCoralModal';
import CoralButton from '../../CoralButtons/CoralButton';
import HexBloom from '../../HexBloom/HexBloom';
import { EDITORIAL_CTA_OVERRIDE, EDITORIAL_INPUT } from '../../editorial';
import { requestMagicLink } from '../../../api/api';

const LazyAuthenticator = lazy(() => import('@aws-amplify/ui-react').then(module => ({
  default: module.Authenticator
})));

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (user: AuthUser) => void;
  initialSignIn?: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onAuthSuccess, initialSignIn = false }) => {
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  // Which view the Amplify Authenticator opens on when the password path mounts.
  // 'forgotPassword' surfaces a first-class reset entry point from the magic-link
  // default view. Defaults to the sign-in/sign-up choice the modal was opened with.
  const passwordDefault = initialSignIn ? 'signIn' : 'signUp';
  const [authInitialState, setAuthInitialState] = useState<'signIn' | 'signUp' | 'forgotPassword'>(
    passwordDefault,
  );
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [message, setMessage] = useState('');

  const handleToggle = () => {
    setUsePasswordLogin(!usePasswordLogin);
    setAuthInitialState(passwordDefault);
    setMessage('');
    setIsEmailSent(false);
  };

  const handleForgotPassword = () => {
    setAuthInitialState('forgotPassword');
    setUsePasswordLogin(true);
    setMessage('');
    setIsEmailSent(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      await requestMagicLink(email);
      setIsEmailSent(true);
      setMessage('Sign-in link sent! Please check your email.');
    } catch (error) {
      setMessage('Error sending sign-in link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const AuthComponents = {
    Header() {
      return null; // Remove the header from Authenticator
    },
  };

  return (
    <GenericCoralModal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center text-center px-2">
        <div aria-hidden="true" className="pointer-events-none select-none opacity-60">
          <HexBloom seed={4} size={120} className="motion-safe:animate-breathe" />
        </div>
        <h2 className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight -mt-2">
          Welcome to Coral
        </h2>

        {!usePasswordLogin ? (
          <div className="w-full max-w-sm px-2">
            {!isEmailSent ? (
              <form onSubmit={handleEmailSubmit} className="mt-6 space-y-4">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={EDITORIAL_INPUT}
                  placeholder="Enter your email"
                  required
                />
                <CoralButton
                  onClick={() => {}}
                  className={`w-full ${EDITORIAL_CTA_OVERRIDE}`}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Continue with Email'}
                </CoralButton>
                {message && <p className="text-sm text-editorial-muted/70">{message}</p>}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-editorial-muted/60 hover:text-editorial-muted underline"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-6 text-center">
                <p className="font-cormorant text-editorial-muted text-xl">Check your email</p>
                <p className="mt-2 text-sm text-editorial-muted/70">{message}</p>
                <p className="mt-3 text-sm text-editorial-muted/70">
                  Didn&apos;t receive the email?
                  <button
                    onClick={() => setIsEmailSent(false)}
                    className="ml-1 text-editorial-accent hover:text-editorial-accent/80 underline"
                  >
                    Try again
                  </button>
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-md mt-6 text-left">
            <Suspense fallback={<div className="text-center py-4 text-editorial-muted/60">Loading...</div>}>
              <LazyAuthenticator
                initialState={authInitialState}
                components={AuthComponents}
                loginMechanisms={['email']}
              >
                {({ user }) => {
                  if (user) {
                    onAuthSuccess(user);
                  }
                  return (<></>); // Return empty element to satisfy typescript
                }}
              </LazyAuthenticator>
            </Suspense>
          </div>
        )}
        <button
          onClick={handleToggle}
          className="mt-6 text-sm text-editorial-muted/60 hover:text-editorial-muted underline"
        >
          {usePasswordLogin ? 'Use passwordless login' : 'Use a password'}
        </button>
      </div>
    </GenericCoralModal>
  );
};

export default AuthModal;
